import { type ThreeEvent, useFrame, useThree } from '@react-three/fiber';
import { CALLBACK_ORDER_AFTER_CAMERA_UPDATE } from './FrameCallbackOrder';
import { useCallback, useEffect, useRef, useState } from 'react';
import * as THREE from 'three';
import { Loader3DTiles, type LoaderOptions, type Runtime } from '@fabric-web/three-loader-3dtiles';
import { useInterval } from '../helpers/hooks';
import type { Number3 } from '../types';
import { useOnClick } from './useOnClick';
import { ViewerLayer } from './ViewerLayer';
import { RENDER_ORDER_IMAGE_VIEWER } from './RenderOrder';

type Props = {
  url: string;
  position?: Number3;
  scale?: Number3;
  rotation?: Number3;
  loaderOptions?: LoaderOptions;
  onClick?: (point: Number3, event: ThreeEvent<PointerEvent>) => void;
};

const loadTiles = ({ url, loaderOptions }: { url: string; loaderOptions?: LoaderOptions }) =>
  Loader3DTiles.load({
    url,
    viewport: {
      width: window.innerWidth,
      height: window.innerHeight,
      devicePixelRatio: window.devicePixelRatio,
    },
    options: {
      ...loaderOptions,
      dracoDecoderPath: 'https://cdn.jsdelivr.net/npm/three@0.160.0/examples/jsm/libs/draco',
      basisTranscoderPath: 'https://cdn.jsdelivr.net/npm/three@0.160.0/examples/jsm/libs/basis',
    },
  });

export const Tileset = ({ url, loaderOptions, onClick, ...props }: Props) => {
  const [model, setModel] = useState<THREE.Object3D<THREE.Object3DEventMap> | null>(null);
  const [runtime, setRuntime] = useState<Runtime | null>(null);

  const mainGroup = useRef<THREE.Group | null>(null);

  const invalidate = useThree((state) => state.invalidate);

  const contentPostProcessOnParent = loaderOptions?.contentPostProcess;

  const isClickable = !!onClick;

  const contentPostProcess = useCallback(
    (content: THREE.Mesh | THREE.Points) => {
      if (isClickable) {
        content.layers.enable(ViewerLayer.AutoPickable);
      }
      content.layers.enable(ViewerLayer.SphericalViewerLayer);
      content.renderOrder = RENDER_ORDER_IMAGE_VIEWER - 1;
      contentPostProcessOnParent?.(content);
    },
    [isClickable, contentPostProcessOnParent]
  );

  useEffect(() => {
    loadTiles({
      url,
      loaderOptions: { ...loaderOptions, contentPostProcess },
    })
      .then((data: { model: THREE.Object3D; runtime: Runtime }) => {
        if (!data) return;
        setModel(data.model);
        setRuntime(data.runtime);
      })
      .catch(() => {});
  }, [url, loaderOptions, contentPostProcess]);

  useInterval(() => invalidate(), 100);

  useFrame(({ camera }, delta) => {
    if (runtime) {
      runtime?.update(delta, camera);
    }
  }, CALLBACK_ORDER_AFTER_CAMERA_UPDATE);

  useEffect(() => {
    return () => runtime?.dispose();
  }, [runtime]);

  const shouldHandle = useCallback((event: ThreeEvent<MouseEvent>) => {
    const { altKey, ctrlKey, metaKey, shiftKey, button } = event;
    const isLeftButton = button === 0;
    if (!isLeftButton || altKey || ctrlKey || metaKey || shiftKey) {
      return false;
    }
    return true;
  }, []);

  const onObjectClick = useCallback(
    (event: ThreeEvent<PointerEvent>) => {
      const intersection = event.intersections[0];
      const { point } = intersection;
      if (onClick) {
        // event.stopPropagation();
        const { x, y, z } = point;
        onClick([x, y, z], event);
      }
    },
    [onClick]
  );

  const { onPointerDown, onPointerUp } = useOnClick({ onClick: onObjectClick, shouldHandle });

  return (
    model &&
    runtime && (
      <group ref={mainGroup} onPointerUp={onPointerUp} onPointerDown={onPointerDown} {...props}>
        <primitive object={model} />
      </group>
    )
  );
};
